/**
 * The languages the add-in ships in, one per src/locales/<tag>.json. Anything
 * else falls back to English.
 */
export const SUPPORTED = [
  "en",
  "bg",
  "cs",
  "da",
  "de",
  "el",
  "es",
  "fi",
  "fr",
  "hr",
  "hu",
  "it",
  "nb",
  "nl",
  "pl",
  "pt",
  "pt-BR",
  "ro",
  "sk",
  "sv",
] as const;
export type Locale = (typeof SUPPORTED)[number];

export const FALLBACK: Locale = "en";

/**
 * Outlook reports Norwegian as nb-NO, but older builds and browsers say "no" or
 * "nn"; Bokmål is the only Norwegian catalogue, so all three land there.
 */
const ALIASES: Record<string, Locale> = { no: "nb", nn: "nb" };

/**
 * Reduce a BCP-47 tag to a language we ship. Outlook reports regional tags
 * ("de-AT"); we translate per language, not per region — except where a
 * regional catalogue exists, so "pt-BR" keeps Brazilian Portuguese while
 * "pt-PT" and bare "pt" get the European one.
 */
export function resolveLocale(tag: string | undefined | null): Locale {
  const normalized = (tag ?? "").replace(/_/g, "-").toLowerCase();
  const exact = SUPPORTED.find((l) => l.toLowerCase() === normalized);
  if (exact) return exact;
  const base = normalized.split("-")[0];
  if (ALIASES[base]) return ALIASES[base];
  return (SUPPORTED as readonly string[]).includes(base) ? (base as Locale) : FALLBACK;
}

/** Human names in their own language, for anywhere a language can be chosen. */
export const LOCALE_NAMES: Record<Locale, string> = {
  en: "English",
  bg: "Български",
  cs: "Čeština",
  da: "Dansk",
  de: "Deutsch",
  el: "Ελληνικά",
  es: "Español",
  fi: "Suomi",
  fr: "Français",
  hr: "Hrvatski",
  hu: "Magyar",
  it: "Italiano",
  nb: "Norsk bokmål",
  nl: "Nederlands",
  pl: "Polski",
  pt: "Português",
  "pt-BR": "Português (Brasil)",
  ro: "Română",
  sk: "Slovenčina",
  sv: "Svenska",
};
